import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../hooks/AuthContext';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminUsers.css';

const AUTO_REFRESH_INTERVAL_MS = 5000;

const initialFilters = {
  status: '',
  internal_test: '',
};

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

const AdminUsersPage = () => {
  const { authenticatedUser } = useAuthContext();
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    users: [],
    pagination: {
      page: 1,
      page_size: 25,
      total_count: 0,
    },
    lastUpdatedAt: null,
  });
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({
    enabled: Boolean(authenticatedUser?.token),
    intervalMs: AUTO_REFRESH_INTERVAL_MS,
  });

  useEffect(() => {
    if (!authenticatedUser?.token) {
      return undefined;
    }

    const controller = new AbortController();

    const loadUsers = async ({ background = false } = {}) => {
      setState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const payload = {
          query,
          page: 1,
          page_size: 25,
          filters: {
            ...(filters.status ? { status: filters.status } : {}),
            ...(filters.internal_test === '' ? {} : { internal_test: filters.internal_test === 'true' }),
          },
        };

        const response = await fetch('/.netlify/functions/admin-search-users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authenticatedUser.token}`,
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load users.');
        }

        setState({
          loading: false,
          refreshing: false,
          error: '',
          users: data.users || [],
          pagination: data.pagination || {
            page: 1,
            page_size: 25,
            total_count: 0,
          },
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState((current) => ({
          ...current,
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load users.',
          users: [],
          lastUpdatedAt: null,
        }));
      }
    };

    loadUsers({ background: refreshNonce > 0 });

    return () => controller.abort();
  }, [authenticatedUser, filters, query, refreshNonce]);

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">User Management</p>
          <h2 className="cq-admin-page__title">Users</h2>
          <p className="cq-admin-page__copy">Find a user, inspect support context, and open the full admin detail view.</p>
        </div>
        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row">
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.loading || state.refreshing}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {state.refreshing ? 'Refreshing...' : `Last updated ${formatDateTime(state.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      <div className="cq-admin-panel cq-admin-users-toolbar">
        <label className="cq-admin-field">
          <span>Search</span>
          <input
            className="cq-admin-input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Email or user ID"
          />
        </label>

        <label className="cq-admin-field">
          <span>Status</span>
          <select
            className="cq-admin-input"
            value={filters.status}
            onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
        </label>

        <label className="cq-admin-field">
          <span>Internal/Test</span>
          <select
            className="cq-admin-input"
            value={filters.internal_test}
            onChange={(event) => setFilters((current) => ({ ...current, internal_test: event.target.value }))}
          >
            <option value="">All</option>
            <option value="true">Internal/Test Only</option>
            <option value="false">External Users</option>
          </select>
        </label>
      </div>

      <div className="cq-admin-panel">
        {state.loading ? <div className="cq-admin-empty-inline">Loading users...</div> : null}
        {!state.loading && state.error ? <div className="cq-admin-empty-inline">{state.error}</div> : null}
        {!state.loading && !state.error && !state.users.length ? <div className="cq-admin-empty-inline">No users found.</div> : null}

        {!state.loading && !state.error && state.users.length ? (
          <>
            <div className="cq-admin-table-wrap">
              <table className="cq-admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>User ID</th>
                    <th>Created</th>
                    <th>Last Active</th>
                    <th>Credits</th>
                    <th>Games</th>
                    <th>Payments</th>
                    <th>Status</th>
                    <th>Internal/Test</th>
                    <th>Admin</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {state.users.map((user) => (
                    <tr key={user.user_id}>
                      <td>{user.email || 'Unknown'}</td>
                      <td className="cq-admin-cell-id">{user.user_id}</td>
                      <td>{formatDateTime(user.created_at)}</td>
                      <td>{formatDateTime(user.last_active_at)}</td>
                      <td>{user.credit_balance}</td>
                      <td>{user.games_played}</td>
                      <td>{user.payments_count}</td>
                      <td>
                        <span className={`cq-admin-badge cq-admin-badge--${user.status}`}>{user.status}</span>
                      </td>
                      <td>{user.is_internal_test ? 'Yes' : 'No'}</td>
                      <td>{user.is_admin ? 'Yes' : 'No'}</td>
                      <td>
                        <Link className="cq-admin-card-link" to={`/admin/users/${user.user_id}`}>View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="cq-admin-users-count">{state.pagination.total_count} user{state.pagination.total_count === 1 ? '' : 's'} found.</p>
          </>
        ) : null}
      </div>
    </section>
  );
};

export default AdminUsersPage;
